import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import AccountAPI from '../fetch/accountApi';
import type { AccountInfo, StaticPage, Uuid } from '../types';

export const useAccountInfo = () => {
  const { i18n } = useTranslation();
  return useQuery<AccountInfo>({
    queryKey: ['accountInfo', i18n.language],
    queryFn: AccountAPI.getAccountInfo,
    staleTime: 5 * 60 * 1000,
  });
};

export const useStaticPages = () => {
  const { i18n } = useTranslation();
  return useQuery<StaticPage[]>({
    queryKey: ['staticPages', i18n.language],
    queryFn: AccountAPI.getStaticPages,
    staleTime: 5 * 60 * 1000,
  });
};

export const useStaticPage = (id: Uuid | null | undefined) => {
  const { i18n } = useTranslation();
  return useQuery<StaticPage>({
    queryKey: ['staticPage', id, i18n.language],
    queryFn: () => AccountAPI.getStaticPage(id as Uuid),
    // Pages with resolved f2m-asset image URLs must refetch before those
    // presigned URLs expire (mirrors Album#pics_url_expires_at precedent),
    // with a 5s safety margin. Pages without embedded assets fall back to
    // the previous 5-minute staleTime.
    staleTime: (query) => {
      const exp = query.state.data?.assets_expire_at;
      if (!exp) return 5 * 60 * 1000;
      return Math.max(0, new Date(exp).getTime() - Date.now() - 5000);
    },
    enabled: !!id,
  });
};

export const useStaticPageBySlug = (slug: string | undefined) => {
  const { i18n } = useTranslation();
  return useQuery<StaticPage>({
    queryKey: ['staticPageBySlug', slug, i18n.language],
    queryFn: () => AccountAPI.getStaticPageBySlug(slug as string),
    // Same presigned-asset-URL expiry handling as useStaticPage above.
    staleTime: (query) => {
      const exp = query.state.data?.assets_expire_at;
      if (!exp) return 5 * 60 * 1000;
      return Math.max(0, new Date(exp).getTime() - Date.now() - 5000);
    },
    enabled: !!slug,
  });
};

// 404 (no page designated as homepage) is a normal outcome, not a transient
// failure — retry: false so Home doesn't hang in a loading state retrying a
// request that will never succeed.
export const useHomepageStaticPage = () => {
  const { i18n } = useTranslation();
  return useQuery<StaticPage>({
    queryKey: ['staticPageHomepage', i18n.language],
    queryFn: AccountAPI.getStaticPageHomepage,
    retry: false,
    // Same presigned-asset-URL expiry handling as useStaticPage above.
    staleTime: (query) => {
      const exp = query.state.data?.assets_expire_at;
      if (!exp) return 5 * 60 * 1000;
      return Math.max(0, new Date(exp).getTime() - Date.now() - 5000);
    },
  });
};
